import React, { useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import type { Answer } from 'types/answers'
import { QuoteChips } from 'components/answers/QuoteLine'
import { quotePreviewForAnswer } from 'components/answers/quotePreview'
import { formatRelativeTimeLabel } from 'lib/relativeTimeLabel'
import styles from './reply-target-quote.module.scss'

interface ReplyTargetQuoteProps {
    answer?: Partial<Answer> | null
    className?: string
}

// The reply modal's target, in the quote chrome the sent message will wear:
// rail, author in the reply family's purple, the post's age, its action tags
// and media as the same small placeholders — then the body opened to three
// lines. The one-line QuoteLine is the same grammar closed; this is it opened
// far enough to read what you are answering without the modal becoming a
// reader. Nothing boxed, and the composer below is untouched.
const ReplyTargetQuote: React.FC<ReplyTargetQuoteProps> = ({ answer, className }) => {
    const preview = useMemo(() => quotePreviewForAnswer(answer), [answer])
    // A thumbnail that fails at load time is only knowable here, so the block —
    // not the pure preview — decides whether a media-only quote has to say what
    // it is rather than sit as a bare glyph.
    const [leadThumbFailed, setLeadThumbFailed] = useState(false)
    useEffect(() => setLeadThumbFailed(false), [preview.media[0]?.thumbUrl])

    const unavailable = !!(answer?.deleted || answer?.archive)
    const authorName = answer?.user?.name
    const timeLabel = formatRelativeTimeLabel(answer?.created_at)
    const hasChips = preview.actionTags.length > 0 || preview.media.length > 0
    const text =
        preview.text || (leadThumbFailed && preview.media.length ? preview.media[0].label : '')

    return (
        <div
            className={cn(styles.replyTargetQuote, className)}
            data-testid="reply-target-quote"
            data-quote-unavailable={unavailable ? 'true' : undefined}
        >
            <span className={styles.screenReaderLabel}>Replying to</span>
            <span className={styles.rail} aria-hidden="true" />
            <div className={styles.body}>
                {unavailable ? (
                    <div className={styles.unavailable}>Original message unavailable</div>
                ) : (
                    <>
                        <div className={styles.head}>
                            {authorName && <span className={styles.author}>{authorName}</span>}
                            {timeLabel && <span className={styles.time}>{timeLabel}</span>}
                            <QuoteChips
                                actionTags={preview.actionTags}
                                media={preview.media}
                                overflowCount={preview.overflowCount}
                                onLeadThumbFailed={() => setLeadThumbFailed(true)}
                            />
                        </div>
                        {/* Chips alone can carry a wordless post — a photo with
                            no caption has nothing to clamp. */}
                        {(text || !hasChips) && (
                            <div className={styles.text}>{text || 'Loading…'}</div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default ReplyTargetQuote
